import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale,
  Colors,
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import './AIUsageGraphs.css'; // Reuse existing styles
import useAuthStore from '../../store/authStore';
import { useTheme } from '../../contexts/ThemeContext';
import financialSubmissionService, { SubmittedFinancialData } from '../../services/financialSubmissionService';

// Register Chart.js components including Colors plugin
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale,
  Colors
);

// Interface for component props
interface NewDashboardChartsProps {
  selectedUserId?: number | null;
}

const NewDashboardCharts: React.FC<NewDashboardChartsProps> = ({ selectedUserId }) => {
  const { user } = useAuthStore();
  const { theme } = useTheme();
  
  // Tab state
  const [activeTab, setActiveTab] = useState<string>('monthly-completeness');
  
  // State for submission data
  const [submissions, setSubmissions] = useState<SubmittedFinancialData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [accessibleLocations, setAccessibleLocations] = useState<any[]>([]);

  // Fetch submission data
  const fetchSubmissionData = async (isFilterChange = false) => {
    if (!isFilterChange) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    try {
      console.log('📊 Fetching submission data for dashboard...');
      
      // Fetch submitted financial data
      const submissionData = await financialSubmissionService.getFinancialSubmissions();
      
      console.log('📈 Dashboard data loaded:', {
        submissions: submissionData.length,
        user: user?.email
      });

      setSubmissions(submissionData);
    } catch (err) {
      console.error('❌ Error fetching dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch accessible locations
  const fetchAccessibleLocations = useCallback(async () => {
    if (!user) return;

    console.log('🔄 Fetching accessible locations for dashboard...');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/accessible-locations`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAccessibleLocations(data.data);
          console.log('📍 Accessible locations loaded:', data.data.length);
        }
      }
    } catch (err) {
      console.error('Error fetching accessible locations:', err);
      setAccessibleLocations([]);
    }
  }, [user]);

  // Helper function to get location name
  const getLocationName = useCallback((locationId?: number) => {
    if (!locationId) return 'Organization Level';
    const location = accessibleLocations.find(loc => loc.id === locationId);
    return location ? location.name : `Location ${locationId}`;
  }, [accessibleLocations]);

  // Load data on component mount
  useEffect(() => {
    fetchAccessibleLocations();
  }, [fetchAccessibleLocations]);

  useEffect(() => {
    // Only fetch submissions after locations are loaded or if user doesn't need location filtering
    if (accessibleLocations.length >= 0) {
      fetchSubmissionData();
    }
  }, [selectedUserId, accessibleLocations]);

  // Helper function to get month name (moved before chartData to avoid hoisting issues)
  const getMonthName = useCallback((month: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1] || month.toString();
  }, []);

  // Chart data processing focused on submission completeness
  const chartData = useMemo(() => {
    if (!submissions.length || !accessibleLocations.length) return null;

    // Get all unique months from submissions
    const monthSet = new Set(submissions.map(sub => `${sub.year}-${String(sub.month).padStart(2, '0')}`));
    const allMonths = Array.from(monthSet).sort();
    
    // Get all locations that should be submitting
    const allLocations = accessibleLocations.map(loc => loc.id);
    const totalLocations = allLocations.length;

    // 1. Monthly Submission Completeness Analysis
    const monthlyCompleteness = allMonths.map(monthKey => {
      const [year, month] = monthKey.split('-');
      const monthSubmissions = submissions.filter(sub => 
        sub.year === parseInt(year) && sub.month === parseInt(month)
      );
      
      const locationsWithSubmissions = new Set(monthSubmissions.map(sub => sub.locationId));
      const submittedLocations = locationsWithSubmissions.size;
      const completionRate = (submittedLocations / totalLocations) * 100;
      
      return {
        month: monthKey,
        monthName: `${getMonthName(parseInt(month))} ${year}`,
        submittedLocations,
        totalLocations,
        completionRate,
        isFullySubmitted: submittedLocations === totalLocations,
        missingLocations: totalLocations - submittedLocations
      };
    });

    // 2. Submission Status by Month
    const submissionStatusByMonth = monthlyCompleteness.reduce((acc, item) => {
      const status = item.isFullySubmitted ? 'Fully Submitted' : 
                    item.submittedLocations > 0 ? 'Partially Submitted' : 'No Submissions';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // 3. Location Participation Analysis
    const locationParticipation = accessibleLocations.map(location => {
      const locationSubmissions = submissions.filter(sub => sub.locationId === location.id);
      const monthsSubmittedSet = new Set(locationSubmissions.map(sub => 
        `${sub.year}-${String(sub.month).padStart(2, '0')}`
      ));
      const monthsSubmitted = monthsSubmittedSet.size;
      
      return {
        locationName: location.name,
        monthsSubmitted,
        totalMonths: allMonths.length,
        participationRate: (monthsSubmitted / allMonths.length) * 100
      };
    });

    // 4. Monthly Completion Rates for Chart
    const completionRatesData = monthlyCompleteness.map(item => ({
      month: item.monthName,
      rate: Math.round(item.completionRate)
    }));

    return {
      monthlyCompleteness,
      submissionStatusByMonth,
      locationParticipation,
      completionRatesData,
      totalMonths: allMonths.length,
      fullySubmittedMonths: monthlyCompleteness.filter(m => m.isFullySubmitted).length,
      partiallySubmittedMonths: monthlyCompleteness.filter(m => !m.isFullySubmitted && m.submittedLocations > 0).length,
      noSubmissionMonths: monthlyCompleteness.filter(m => m.submittedLocations === 0).length
    };
  }, [submissions, accessibleLocations, getLocationName, getMonthName]);

  // Chart options factory
  const getChartOptions = (title: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      colors: {
        enabled: true,
        forceOverride: true
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 16,
          weight: 'bold' as const
        },
        color: theme === 'dark' ? '#ffffff' : '#2a4065'
      },
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 12
          },
          color: theme === 'dark' ? '#ffffff' : '#333333'
        }
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: theme === 'dark' ? '#ffffff' : '#000000',
        bodyColor: theme === 'dark' ? '#ffffff' : '#000000',
        borderColor: theme === 'dark' ? '#444444' : '#cccccc',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        grid: {
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          color: theme === 'dark' ? '#ffffff' : '#333333'
        }
      },
      y: {
        grid: {
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          color: theme === 'dark' ? '#ffffff' : '#333333'
        }
      }
    }
  });

  // Doughnut/Pie chart options
  const doughnutOptions = (title: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      colors: {
        enabled: true,
        forceOverride: true
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 16,
          weight: 'bold' as const
        },
        color: theme === 'dark' ? '#ffffff' : '#2a4065'
      },
      legend: {
        position: 'bottom' as const,
        labels: {
          font: {
            size: 11
          },
          color: theme === 'dark' ? '#ffffff' : '#333333',
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: theme === 'dark' ? '#ffffff' : '#000000',
        bodyColor: theme === 'dark' ? '#ffffff' : '#000000'
      }
    }
  });

  // Prepare chart datasets focused on submission completeness
  const monthlyCompletionRatesData = chartData ? {
    labels: chartData.completionRatesData.map(item => item.month),
    datasets: [{
      label: 'Completion Rate (%)',
      data: chartData.completionRatesData.map(item => item.rate),
      fill: true
    }]
  } : { labels: [], datasets: [] };

  const submissionStatusData = chartData ? {
    labels: Object.keys(chartData.submissionStatusByMonth),
    datasets: [{
      data: Object.values(chartData.submissionStatusByMonth)
    }]
  } : { labels: [], datasets: [] };

  const locationParticipationData = chartData ? {
    labels: chartData.locationParticipation.map(loc => loc.locationName),
    datasets: [{
      label: 'Participation Rate (%)',
      data: chartData.locationParticipation.map(loc => Math.round(loc.participationRate))
    }]
  } : { labels: [], datasets: [] };

  const completenessOverviewData = chartData ? {
    labels: ['Fully Submitted', 'Partially Submitted', 'No Submissions'],
    datasets: [{
      label: 'Months',
      data: [
        chartData.fullySubmittedMonths,
        chartData.partiallySubmittedMonths,
        chartData.noSubmissionMonths
      ]
    }]
  } : { labels: [], datasets: [] };

  if (loading) {
    return (
      <div className="dashboard-charts-container">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading submission data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-charts-container">
        <div className="dashboard-error">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={() => fetchSubmissionData()} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!submissions.length) {
    return (
      <div className="dashboard-charts-container">
        <div className="no-data-message">
          <div className="no-data-icon">📊</div>
          <h3>No Submission Data Available</h3>
          <p>Submit some financial data to see dashboard visualizations.</p>
        </div>
      </div>
    );
  }

  // Define available tabs
  const tabs = [
    {
      id: 'monthly-completeness',
      label: '📅 Monthly Submission Completeness',
      description: 'Track which months have complete submissions from all locations'
    },
    {
      id: 'performance-overview',
      label: '📊 Performance Overview',
      description: 'View overall system performance and metrics'
    },
    {
      id: 'location-analysis',
      label: '🏢 Location Analysis',
      description: 'Analyze performance by location and region'
    },
    {
      id: 'trends-insights',
      label: '📈 Trends & Insights',
      description: 'Discover patterns and trends in your data'
    }
  ];

  const currentTab = tabs.find(tab => tab.id === activeTab);

  return (
    <div className="dashboard-charts-container">
      {/* Header */}
      <div className="dashboard-header-section">
        <h2>🎯 Analytics Dashboard</h2>
        <p>Comprehensive insights and analytics for your organization</p>
      </div>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        <div className="tab-list">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Current Tab Description */}
        <div className="tab-description">
          <p>{currentTab?.description}</p>
        </div>

        {/* Monthly Submission Completeness Tab */}
        {activeTab === 'monthly-completeness' && (
          <>
            {/* Data Insights - Moved to very top */}
      <div className="insights-section">
        <h3>💡 Submission Completeness Insights</h3>
        <div className="insights-grid">
          <div className="insight-card">
            <div className="insight-icon">✅</div>
            <div className="insight-content">
              <h4>Best Performing Month</h4>
              <p>
                {chartData && chartData.monthlyCompleteness && chartData.monthlyCompleteness.length > 0
                  ? chartData.monthlyCompleteness
                      .sort((a, b) => b.completionRate - a.completionRate)[0]?.monthName || 'No data'
                  : 'No data available'
                }
              </p>
            </div>
          </div>
          
          <div className="insight-card">
            <div className="insight-icon">⚠️</div>
            <div className="insight-content">
              <h4>Months Needing Attention</h4>
              <p>
                {chartData?.partiallySubmittedMonths || 0} months have incomplete submissions
              </p>
            </div>
          </div>
          
          <div className="insight-card">
            <div className="insight-icon">🏢</div>
            <div className="insight-content">
              <h4>Most Consistent Location</h4>
              <p>
                {chartData && chartData.locationParticipation && chartData.locationParticipation.length > 0
                  ? chartData.locationParticipation
                      .sort((a, b) => b.participationRate - a.participationRate)[0]?.locationName || 'No data'
                  : 'No data available'
                }
              </p>
            </div>
          </div>
          
          <div className="insight-card">
            <div className="insight-icon">📊</div>
            <div className="insight-content">
              <h4>Missing Submissions</h4>
              <p>
                {chartData && chartData.monthlyCompleteness 
                  ? chartData.monthlyCompleteness.reduce((acc, month) => acc + month.missingLocations, 0)
                  : 0
                }{' '}
                total missing location submissions
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Monthly Breakdown - Moved to top */}
      {chartData && chartData.monthlyCompleteness && (
        <div className="insights-section" style={{ marginTop: '2rem' }}>
          <h3>📅 Monthly Breakdown</h3>
          <div className="monthly-breakdown">
            {chartData.monthlyCompleteness.map((month, index) => (
              <div key={month.month} className={`month-card ${month.isFullySubmitted ? 'complete' : 'incomplete'}`}>
                <div className="month-header">
                  <h4>{month.monthName}</h4>
                  <div className={`status-badge ${month.isFullySubmitted ? 'complete' : 'incomplete'}`}>
                    {month.isFullySubmitted ? '✅ Complete' : '⚠️ Incomplete'}
                  </div>
                </div>
                <div className="month-stats">
                  <span>{month.submittedLocations}/{month.totalLocations} locations</span>
                  <span>{Math.round(month.completionRate)}% complete</span>
                  {month.missingLocations > 0 && (
                    <span className="missing">{month.missingLocations} missing</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div className="dashboard-controls">
        <button 
          onClick={() => fetchSubmissionData(true)} 
          disabled={isRefreshing}
          className="refresh-button"
        >
          {isRefreshing ? '🔄 Refreshing...' : '🔄 Refresh Data'}
        </button>
      </div>
          </>
        )}

        {/* Performance Overview Tab */}
        {activeTab === 'performance-overview' && (
          <div className="tab-placeholder">
            <div className="placeholder-content">
              <h3>📊 Performance Overview</h3>
              <p>This dashboard will show overall system performance metrics, KPIs, and operational insights.</p>
              <div className="coming-soon">
                <span>🚧 Coming Soon</span>
                <p>Performance metrics and system overview charts will be available here.</p>
              </div>
            </div>
          </div>
        )}

        {/* Location Analysis Tab */}
        {activeTab === 'location-analysis' && (
          <div className="tab-placeholder">
            <div className="placeholder-content">
              <h3>🏢 Location Analysis</h3>
              <p>This dashboard will provide detailed analysis by location, regional comparisons, and location-specific insights.</p>
              <div className="coming-soon">
                <span>🚧 Coming Soon</span>
                <p>Location-based analytics and regional performance comparisons will be available here.</p>
              </div>
            </div>
          </div>
        )}

        {/* Trends & Insights Tab */}
        {activeTab === 'trends-insights' && (
          <div className="tab-placeholder">
            <div className="placeholder-content">
              <h3>📈 Trends & Insights</h3>
              <p>This dashboard will reveal patterns, trends, and predictive insights from your data over time.</p>
              <div className="coming-soon">
                <span>🚧 Coming Soon</span>
                <p>Advanced analytics, trend analysis, and predictive insights will be available here.</p>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default NewDashboardCharts;
